import { useState, useEffect } from "react";
import API from "../../api/axiosConfig";
import { motion } from "framer-motion";
import { toast } from "react-toastify";
import { ImageIcon, Bike, Truck } from "lucide-react";
import { getImageUrl } from "../../utils/getImageUrl";

export default function EditCompanyModal({ company, onClose, onUpdated }) {
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    name:           company.name        || "",
    slug:           company.slug        || "",
    description:    company.description || "",
    category:
      typeof company.category === "object"
        ? company.category.id
        : company.categoryId ?? company.category ?? "",
    hasOwnDelivery: company.hasOwnDelivery ?? false,
    deliveryFee:    company.deliveryFee != null ? String(company.deliveryFee) : "",
    logo: null,
  });

  const [previewLogo, setPreviewLogo] = useState(
    company.logo ? getImageUrl(company.logo) : ""
  );

  const [categories, setCategories] = useState([]);

  useEffect(() => {
    API.get("/categories")
      .then((res) =>
        setCategories(
          res.data?.data?.categories ?? res.data?.data?.data ?? res.data?.data ?? []
        )
      )
      .catch(() => toast.error("Error cargando categorías"));
  }, []);

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setForm({ ...form, logo: file });
    setPreviewLogo(URL.createObjectURL(file));
  };

  const handleSave = async () => {
    if (!form.name || !form.slug || !form.category) {
      toast.warning("Completa los campos obligatorios");
      return;
    }

    try {
      setLoading(true);

      const fd = new FormData();
      fd.append("name",           form.name);
      fd.append("slug",           form.slug);
      fd.append("description",    form.description);
      fd.append("category",       form.category);
      fd.append("hasOwnDelivery", String(form.hasOwnDelivery));
      fd.append("deliveryFee",    form.deliveryFee);
      if (form.logo instanceof File) fd.append("logo", form.logo);

      await API.put(`/companies/${company.id}`, fd);

      const refreshed = await API.get(`/companies/by-id/${company.id}`);
      toast.success("Empresa actualizada");
      onUpdated(refreshed.data?.data?.company ?? refreshed.data?.data ?? {});
      onClose();
    } catch (err) {
      console.error(err);
      toast.error("Error actualizando empresa");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col"
      >
        {/* Header */}
        <div className="px-6 py-4 border-b flex justify-between items-center flex-shrink-0">
          <h2 className="text-lg font-bold text-gray-900">Editar empresa</h2>
          <button type="button" onClick={onClose}
            className="text-gray-400 hover:text-gray-700 text-xl leading-none">✕</button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">

          {/* ── Información general ── */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <div className="md:col-span-2 space-y-4">
              <div>
                <label className="text-xs font-semibold text-gray-500 mb-1 block">Nombre *</label>
                <input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Nombre de la empresa"
                  className="border border-gray-200 rounded-xl px-4 py-2.5 w-full text-sm focus:outline-none focus:ring-2 focus:ring-supernova-300"
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-500 mb-1 block">Slug</label>
                <input
                  value={form.slug}
                  readOnly
                  className="border border-gray-100 rounded-xl px-4 py-2.5 w-full text-sm bg-gray-50 text-gray-400 cursor-not-allowed"
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-500 mb-1 block">Descripción</label>
                <textarea
                  rows={3}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Descripción de la empresa"
                  className="border border-gray-200 rounded-xl px-4 py-2.5 w-full text-sm resize-none focus:outline-none focus:ring-2 focus:ring-supernova-300"
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-500 mb-1 block">Categoría *</label>
                <select
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="border border-gray-200 rounded-xl px-4 py-2.5 w-full text-sm bg-white focus:outline-none focus:ring-2 focus:ring-supernova-300"
                >
                  <option value="">Seleccionar categoría</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Logo */}
            <div className="flex flex-col items-center gap-3 pt-1">
              <div className="w-28 h-28 border-2 border-dashed border-gray-200 rounded-2xl bg-gray-50 flex items-center justify-center overflow-hidden">
                {previewLogo ? (
                  <img src={previewLogo} alt="Logo" className="w-full h-full object-cover" />
                ) : (
                  <ImageIcon size={36} className="text-gray-300" />
                )}
              </div>
              <label className="cursor-pointer flex items-center gap-1.5 text-xs font-semibold text-supernova-600 hover:text-supernova-700 transition">
                <ImageIcon size={14} />
                Cambiar logo
                <input type="file" hidden accept="image/*" onChange={handleLogoUpload} />
              </label>
            </div>
          </div>

          {/* ── Configuración de domicilio ── */}
          <div className="border border-gray-100 rounded-2xl p-4 space-y-4">
            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">
              Configuración de domicilio
            </p>

            {/* Toggle hasOwnDelivery */}
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0
                  ${form.hasOwnDelivery ? "bg-green-100" : "bg-gray-100"}`}>
                  <Bike size={18} className={form.hasOwnDelivery ? "text-green-600" : "text-gray-400"} />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-800">Tengo domiciliario propio</p>
                  <p className="text-[11px] text-gray-400 leading-tight">
                    Los clientes podrán elegir envío con tu repartidor (gratis)
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setForm({ ...form, hasOwnDelivery: !form.hasOwnDelivery })}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200
                  ${form.hasOwnDelivery ? "bg-green-500" : "bg-gray-200"}`}
              >
                <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200
                  ${form.hasOwnDelivery ? "translate-x-5" : "translate-x-0"}`} />
              </button>
            </div>

            {/* Tarifa domicilio general */}
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                <Truck size={18} className="text-blue-500" />
              </div>
              <div className="flex-1">
                <label className="text-sm font-semibold text-gray-800 block mb-1">
                  Tarifa domicilio general
                </label>
                <p className="text-[11px] text-gray-400 mb-2 leading-tight">
                  Costo del envío con ryder de la plataforma. Dejar vacío usa la tarifa por defecto.
                </p>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-400 font-medium">$</span>
                  <input
                    type="number"
                    min="0"
                    step="100"
                    value={form.deliveryFee}
                    onChange={(e) => setForm({ ...form, deliveryFee: e.target.value })}
                    placeholder="Ej: 5000"
                    className="border border-gray-200 rounded-xl pl-7 pr-4 py-2.5 w-full text-sm focus:outline-none focus:ring-2 focus:ring-supernova-300"
                  />
                </div>
              </div>
            </div>
          </div>

        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t flex justify-end gap-3 flex-shrink-0">
          <button onClick={onClose}
            className="px-5 py-2 border border-gray-200 rounded-xl text-sm font-semibold text-gray-600 hover:bg-gray-50 transition">
            Cancelar
          </button>
          <button onClick={handleSave} disabled={loading}
            className="px-6 py-2 rounded-xl bg-supernova-500 hover:bg-supernova-600 text-white text-sm font-semibold transition disabled:opacity-60">
            {loading ? "Guardando..." : "Guardar cambios"}
          </button>
        </div>
      </motion.div>
    </div>
  );
}
